/**
 * A spreadsheet as a spreadsheet.
 *
 * The grid coordinates are the parser's, not this component's: a workbook whose data starts
 * at C5 shows C5, and a row the parser dropped leaves a gap in the numbering rather than
 * shifting everything after it. Deriving either from an array index would be wrong in
 * exactly the cases that matter and silently right everywhere else.
 *
 * Every table in this console goes through `DataTable`, which is where column resizing and
 * its persistence live. The table key includes the unit, so widths are remembered per sheet
 * instead of bleeding across a sheet switch.
 */
import { useMemo, useState } from "react";
import { DataTable, type Column } from "./DataTable";
import { Empty } from "./ui";
import type { DocumentStructure, Grid } from "../lib/types";

interface SheetTable {
  unit: number;
  label: string;
  header: string[];
  rows: string[][];
  grid: Grid | null;
}

function sheetsOf(structure: DocumentStructure): SheetTable[] {
  const out: SheetTable[] = [];
  for (const placed of structure.blocks) {
    if (placed.block.kind !== "table") continue;
    out.push({
      unit: placed.origin.unit,
      label: placed.origin.unit_label ?? `sheet ${placed.origin.unit + 1}`,
      header: placed.block.header,
      rows: placed.block.rows,
      grid: placed.block.grid ?? null,
    });
  }
  return out;
}

type Row = { n: string; cells: string[] };

export function SheetView({ structure }: { structure: DocumentStructure }) {
  const sheets = useMemo(() => sheetsOf(structure), [structure]);
  const [active, setActive] = useState(0);

  if (sheets.length === 0) {
    return <Empty title="no cells" hint="this document parsed, but produced no table" />;
  }

  const sheet = sheets[Math.min(active, sheets.length - 1)];
  if (!sheet) {
    return <Empty title="no cells" hint="this document parsed, but produced no table" />;
  }
  const rows: Row[] = sheet.rows.map((cells, i) => ({
    // The file's own row number where we have it; a bare ordinal only when we do not.
    n: sheet.grid ? String(sheet.grid.rows[i] ?? "") : String(i + 1),
    cells,
  }));

  const columns: Column<Row>[] = [
    {
      key: "__row",
      header: "",
      width: 56,
      align: "end",
      cellClass: "font-mono text-[11px] text-faint tabular-nums",
      render: (row) => row.n,
    },
    ...sheet.header.map((label, i) => ({
      key: `c${i}`,
      header: (
        <span className="flex items-baseline gap-1.5">
          {sheet.grid?.columns[i] && (
            <span className="font-mono text-[9.5px] text-faint">{sheet.grid.columns[i]}</span>
          )}
          <span className="truncate">{label || " "}</span>
        </span>
      ),
      width: 180,
      render: (row: Row) => row.cells[i] ?? "",
    })),
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {sheets.length > 1 && (
        <div className="flex shrink-0 items-stretch gap-px overflow-x-auto border-b border-hair bg-hair">
          {sheets.map((s, i) => (
            <button
              key={`${s.unit}-${i}`}
              type="button"
              onClick={() => setActive(i)}
              className={`bg-ground px-3 py-1.5 font-mono text-[11px] whitespace-nowrap transition-colors ${
                i === active ? "text-gold" : "text-faint hover:text-dim"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      <DataTable
        table={`sheet:${sheet.unit}`}
        columns={columns}
        rows={rows}
        rowKey={(row, i) => `${row.n}:${i}`}
        fill
      />
    </div>
  );
}
